<template>
  <div>
    <ChatBubble v-if="!showChat" />
    <div class="resizeable-area">
      <transition name="chat-open">
        <DxResizable
          id="chatContainer"
          :keep-aspect-ratio="false"
          :min-width="minWidth"
          :min-height="minHeight"
          :max-height="maxHeight"
          :max-width="maxWidth"
          handles="left top topleft"
          :class="showChat ? 'borderVisible' : ''"
          v-if="showChat"
          @resize="onChatResize"
        >
          <div class="chat-container" v-show="showChat">
            <div class="row chat-options justify-between">
              <span class="chat-title bold-font">{{ t("chat.title") }}</span>
              <div class="chat-icon-options-row row justify-center">
                <!--                <i
                  class="dx-icon-copy chat-icon-option"
                  style="margin-right: 4px"
                  @click="copyChat"
                  id="copyChatIcon"
                  :aria-label="t('chat.copy')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#copyChatIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.copy") }}
                </DxTooltip> -->
                <i
                  class="dx-icon-refresh chat-icon-option"
                  style="margin-right: 4px"
                  @click="refreshChat"
                  id="refreshChatIcon"
                  :aria-label="t('chat.refresh')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#refreshChatIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.refresh") }}
                </DxTooltip>
                <i
                  class="dx-icon-fullscreen chat-icon-option"
                  @click="increaseChatSize"
                  id="increaseSizeIcon"
                  :aria-label="t('chat.increase_size')"
                  v-if="!isChatMaximized"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#increaseSizeIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                  v-if="!isChatMaximized"
                >
                  {{ t("chat.increase_size") }}
                </DxTooltip>

                <i
                  class="las la-compress la-2x chat-icon-option"
                  @click="decreaseChatSize"
                  id="decreaseSizeIcon"
                  :aria-label="t('chat.decrease_size')"
                  v-if="isChatMaximized"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#decreaseSizeIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                  v-if="isChatMaximized"
                >
                  {{ t("chat.decrease_size") }}
                </DxTooltip>

                <i
                  class="las la-2x la-history chat-icon-option"
                  @click="showHistoryPopup"
                  id="showHistoryBtn"
                  :aria-label="t('chat.chat_history')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#showHistoryBtn"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.chat_history") }}
                </DxTooltip>

                <i
                  class="dx-icon-close chat-icon-option"
                  @click="closeChat"
                  id="closeChatIcon"
                  :aria-label="t('chat.close')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#closeChatIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.close") }}
                </DxTooltip>
              </div>
            </div>

            <div class="chat-box">
              <DxChat
                :items="visibleMessages"
                :user="currentUser"
                :showAvatar="true"
                :showUserName="true"
                :showDayHeaders="true"
                :showMessageTimestamp="true"
                :dayHeaderFormat="'dd/MM/yyyy'"
                :messageTimestampFormat="'hh:mm'"
                message-template="message"
                @message-entered="onMessageEntered"
                v-model:typingUsers="typingUsers"
                class="chat-content"
                ><template #message="{ data }">
                  <div
                    class="chat-message-text"
                    v-html="renderMessage(data.message)"
                  ></div>
                </template>
              </DxChat>
            </div>
          </div>
        </DxResizable>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, ref, onMounted } from "vue";
import store from "../../store/index";
import { useI18n } from "vue-i18n";
import ChatBubble from "./ChatBubble.vue";
import { DxChat } from "devextreme-vue/chat";
import { DxTooltip } from "devextreme-vue/tooltip";
import DxResizable from "devextreme-vue/resizable";
import moment from "moment";
import AiAssistantService from "../../services/aiAssistant.service";
import MarkdownIt from "markdown-it";

const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: true,
});

const { t, locale } = useI18n();
const typingUsers = computed(() => store.state.chat.typingUsers);
const noResultsText = t("chat.no_results");
const showChat = computed({
  get: () => store.state.chat.showChat,
});
const renderMessage = (message) => {
  if (!message || !message.text) return "";

  return md.render(message.text);
};

const maxHeight = ref(0);
const maxWidth = ref(0);

const updateViewportLimits = () => {
  maxWidth.value = window.innerWidth * 0.9;
  maxHeight.value = window.innerHeight * 0.9;
};

const minWidth = computed(() => {
  if (window.innerWidth < 820) {
    return window.innerWidth * 0.9;
  }
  return 720;
});

const minHeight = computed(() => {
  if (window.innerHeight < 820) {
    return window.innerHeight * 0.9;
  }
  return 620;
});

onMounted(() => {
  updateViewportLimits();
  window.addEventListener("resize", updateViewportLimits);
});

const refreshChat = () => {
  store.dispatch("chat/resetChat");
  store.dispatch("chat/startNewConversation");
};

const closeChat = () => {
  store.commit("chat/updateShowChat", false);
};

const isChatMaximized = ref(false);

const onChatResize = (e) => {
  if (e.width >= maxWidth.value - 10 && e.height >= maxHeight.value - 10)
    isChatMaximized.value = true;
  else isChatMaximized.value = false;
};

const decreaseChatSize = () => {
  const el = document.getElementById("chatContainer");
  if (!el) return;

  el.classList.add("animate-resize");

  el.style.width = `${minWidth.value}px`;
  el.style.height = `${minHeight.value}px`;
  isChatMaximized.value = false;

  setTimeout(() => {
    el.classList.remove("animate-resize");
  }, 200);
};

const increaseChatSize = () => {
  const el = document.getElementById("chatContainer");
  if (!el) return;
  /* 
  Added this class (animate-resize) to do a simple animation when clicking button
  to increase size. Needs to be removed because if always kept, it messes with
  the resizing of chat when dragging, 
  */
  el.classList.add("animate-resize");

  el.style.width = `${maxWidth.value}px`;
  el.style.height = `${maxHeight.value}px`;
  isChatMaximized.value = true;
  setTimeout(() => {
    el.classList.remove("animate-resize");
  }, 200);
};

onBeforeMount(async () => {
  if (store.state.chat.settings === null) await AiAssistantService.getAISettings();
  await store.dispatch("chat/initChatFromSession");
});

const currentUser = computed({
  get: () => store.state.chat.currentUser,
});

const currentConversationMessages = computed(() => {
  const currentConversationId = store.state.chat.currentConversationId;
  return currentConversationId
    ? store.state.chat.messagesByConversation[currentConversationId] ?? []
    : [];
});

const visibleMessages = computed(() => store.getters["chat/visibleMessages"]);

const onMessageEntered = async (e) => {
  const message = e?.message?.text ?? e?.text ?? "";

  if (!message) return;

  store.dispatch("chat/addUserMessage", message);

  if (
    !store.state.chat.aiAssistantAccessToken.accessToken ||
    moment(store.state.chat.aiAssistantAccessToken.expirationTime).isBefore(
      moment(),
    )
  ) {
    await AiAssistantService.getAIAssistantAccessToken()
      .then(async (response) => {
        if (response.status === 200) {
          store.commit("chat/setAIAssistantAccessToken", response.data);
          await AiAssistantService.processPrompt(
            currentConversationMessages.value,
            locale.value,
            noResultsText,
          );
        }
      })
      .catch(() => {
        store.dispatch("chat/addAssistantMessage", t("chat.error_occurred"));
        store.dispatch("chat/startNewConversation");
      });
  } else {
    await AiAssistantService.processPrompt(
      currentConversationMessages.value,
      locale.value,
      noResultsText,
    );
  }
};

const showHistoryPopup = () => {
  store.commit("chat/updateIsHistoryPopupVisible", true);
  closeChat();
};
</script>

<style scoped lang="scss">
.options {
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-width: 280px;
  background-color: rgba(191, 191, 191, 0.15);
  gap: 16px;
}

.caption {
  font-size: 14px;
  font-weight: 500;
}

.option-separator {
  border-bottom: 1px solid #0a108f;
}

.dx-avatar {
  border: 1px solid #0a108f;
}

#chatContainer {
  width: 720px;
  height: 620px;
  bottom: 20px;
  right: 20px;
  position: fixed;
  z-index: 999;
  max-height: 90vh;
  max-width: 93vw;
  /*
  Note: Keep this, translate(0,0) will keep chat in fixed position
  if a better solution is found I'll implement it.
  */
  transform: translate(0, 0) !important;
}
#chatContainer.animate-resize {
  transition: width 0.2s ease, height 0.2s ease;
}
.borderVisible {
  border-radius: 14px;
  background: white;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.chat-open-enter-active {
  transition: opacity 0.35s ease-out, transform 0.35s ease-out;
}

.chat-open-enter-from {
  opacity: 0;
  transform: scale(0.96);
}

.chat-open-enter-to {
  opacity: 1;
  transform: scale(1);
}

.chat-open-leave-active {
  transition: none;
}

#chatContainer {
  transform-origin: top left;
}
</style>
